import { Button, cn, Input } from "@gotts.ai/ui";
import { useState } from "react";

import { PcbCard } from "../../components/PcbCard";

import { validateInput } from "./utils";

interface CustomContractFormProps {
  address: string;
  abiText: string;
  onAddressChange: (v: string) => void;
  onAbiChange: (v: string) => void;
  onClose: () => void;
}

export function CustomContractForm({
  address,
  abiText,
  onAddressChange,
  onAbiChange,
  onClose,
}: CustomContractFormProps) {
  const [abiError, setAbiError] = useState<string | null>(null);
  const addressValidation = validateInput(address, "address");

  const handleAbiChange = (text: string) => {
    onAbiChange(text);
    if (!text) {
      setAbiError(null);
      return;
    }
    try {
      const parsed: unknown = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        setAbiError("ABI must be a JSON array");
      } else {
        setAbiError(null);
      }
    } catch {
      setAbiError(
        "Invalid JSON — check for trailing commas or missing brackets",
      );
    }
  };

  return (
    <PcbCard label="CUSTOM" cut={10}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-[var(--color-text)]">
            Custom Contract
          </span>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-medium text-[var(--color-text-muted)]">
            Contract Address
          </label>
          <Input
            type="text"
            placeholder="0x..."
            value={address}
            onChange={(e) => {
              onAddressChange(e.target.value);
            }}
            className={cn(
              "font-mono text-xs",
              address &&
                (addressValidation.valid ? "input-valid" : "input-invalid"),
            )}
          />
          {address && !addressValidation.valid && addressValidation.error && (
            <p className="text-[9px] text-[var(--color-error)]">
              {addressValidation.error}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-medium text-[var(--color-text-muted)]">
            ABI (JSON)
          </label>
          <textarea
            placeholder="Paste ABI JSON array..."
            value={abiText}
            onChange={(e) => {
              handleAbiChange(e.target.value);
            }}
            rows={5}
            className={cn(
              "text-text-dim placeholder-text-muted w-full border border-[rgba(201,168,76,0.12)] bg-transparent p-2 font-mono text-xs outline-none focus:border-[var(--color-gold)]",
              abiText && !abiError && "input-valid",
              abiError && "input-invalid",
            )}
          />
          {abiError && (
            <p className="text-[9px] text-[var(--color-error)]">{abiError}</p>
          )}
        </div>
      </div>
    </PcbCard>
  );
}
